/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import DashboardHeader from "../../components/layout/DashboardHeader";
import Styles from "./supervisorDashboard.module.scss";
import {
  Badge,
  Button,
  Col,
  Input,
  notification,
  Row,
  Segmented,
  Select,
  Spin,
} from "antd";
import {
  CustomSpin,
  DownloadIcon,
  FilterIcon,
  SearchIcon,
} from "../../assets/icons/icons";
import Icon, { SyncOutlined } from "@ant-design/icons";
import { useGetFlowDashboard, useGetLocationDashboard } from "./queries";
import LocationView from "./locationView";
import _ from "lodash";
import DashboardFilters from "./dashboardFilters";
import FlowView from "./flowView";
import { useMutation } from "@tanstack/react-query";
import { downloadLocationView } from "./mutations";
import { convertUTCToLocal } from "../../utils/timeUtils";

const SupervisorDashboard = () => {
  //state
  const [dashboardView, setDashboardView] = useState<any>(
    localStorage?.getItem("dashboardTab")
  );
  const [payload, setPayload] = useState<any>({
    start_date: null, //convertLocalToUTC(new Date())
    end_date: null, //convertLocalToUTC(new Date())
    use_end_date: null,
    use_start_date: null,
    delivery_start_date: null,
    delivery_end_date: null,
    missed: null,
    delayed: null,
    primary_location: null,
    secondary_location: null,
    tote_status: null,
    trolley_status: null,
    priority_status: null,
    search: null,
    page_size: 5,
    page_no: 1,
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [search, setSearch] = useState<any>(null);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number>(60000);

  // Auto-refresh interval options (in milliseconds)
  const refreshIntervalOptions = [
    { label: "1 min", value: 60000 },
    { label: "5 mins", value: 300000 },
    { label: "10 mins", value: 600000 },
  ];

  // on mount calls
  const {
    data: d1,
    isLoading: l1,
    isFetching: f1,
  } = useGetFlowDashboard(payload, dashboardView, autoRefreshInterval);
  const {
    data: d2,
    isLoading: l2,
    isFetching: f2,
  } = useGetLocationDashboard(payload, dashboardView, autoRefreshInterval);

  // segment options
  const dashboardOptions = [
    { key: "2", label: "Location View ", value: "Location" },
    { key: "1", label: "Flow View", value: "Flow" },
  ];

  const handleSearch = useMemo(
    () =>
      _.debounce((e) => {
        setPayload((prev: any) => ({
          ...prev,
          search: e?.target?.value ? e?.target?.value : null,
        }));
      }, 1000),
    []
  );

  const downloadXlsxTrackView = (data: any) => {
    const trolleyData = data?.details || [];
    const toteData = data?.details_tote || [];

    // -------------------------------
    // FORMAT TROLLEY TABLE
    // -------------------------------
    const trolleyRows = trolleyData.map((row: any) => {
      // Check for "No. of Totes in Trolley" in various possible field names
      const toteCount =
        row["No. of Totes in Trolley"] ??
        row["No of Totes in Trolley"] ??
        row["available_totes"] ??
        row["availableTotes"] ??
        row["total_totes"] ??
        row["totalTotes"] ??
        (row["totes"] && Array.isArray(row["totes"])
          ? row["totes"].length
          : null);

      return {
        "Primary Location": row["Primary Location"] ?? "-",
        "Trolley ID": row["Trolley ID"] ?? "-",
        "Registered Date": row["Registered Date"]
          ? convertUTCToLocal(row["Registered Date"])
          : "-",
        "No. of Totes in Trolley":
          toteCount !== null && toteCount !== undefined ? toteCount : "-",
        "Trolley Priority": row["Priority"] ?? "-",
        "Secondary location(From → To)":
          row["(Secondary) location From → To"] ?? "-",
        Tag: row?.overdue
          ? "Overdue"
          : row?.missed
          ? "Missed Scan"
          : row?.Delayed
          ? "Delayed"
          : "-",
        "Location In": row["Location In"]
          ? convertUTCToLocal(row["Location In"])
          : "-",
        "Location Out": row["Location Out"]
          ? convertUTCToLocal(row["Location Out"])
          : "-",
        "Case Type": row["Case Type"] ?? "-",
      };
    });

    const trolleyHeaders = [
      "Primary Location",
      "Trolley ID",
      "Registered Date",
      "No. of Totes in Trolley",
      "Trolley Priority",
      "Secondary location(From → To)",
      "Tag",
      "Location In",
      "Location Out",
      "Case Type",
    ];

    const ws: any = XLSX.utils.json_to_sheet(trolleyRows, {
      header: trolleyHeaders,
    });

    // Set column widths for trolley table
    ws["!cols"] = [
      { wch: 25 },
      { wch: 15 },
      { wch: 20 },
      { wch: 22 },
      { wch: 18 },
      { wch: 25 },
      { wch: 15 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
    ];

    // -------------------------------
    // STYLE HEADER OF TROLLEY TABLE
    // -------------------------------
    const trolleyRange = XLSX.utils.decode_range(ws["!ref"]);
    for (let C = trolleyRange.s.c; C <= trolleyRange.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c: C });
      if (ws[addr]) {
        ws[addr].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4472C4" } },
          alignment: { horizontal: "center" },
        };
      }
    }

    // -------------------------------------------------------
    // Add 2 BLANK ROWS BEFORE STARTING SECOND TABLE
    // -------------------------------------------------------
    const blankRowIndex = trolleyRows.length + 2;

    trolleyHeaders.forEach((h, i) => {
      const addr = XLSX.utils.encode_cell({ r: blankRowIndex, c: i });
      ws[addr] = { v: "" };
    });

    // -------------------------------
    // FORMAT TOTE TABLE (SECOND TABLE)
    // -------------------------------
    const toteHeaders = [
      "Tote No",
      "Registered Date",
      "Trolley ID",
      "Tote Priority",
      "Secondary location(From → To)",
      "Tag",
      "Location In",
      "Location Out",
      "Case Type",
    ];

    const toteRows = toteData.map((row: any) => ({
      "Tote No": row["Tote No"] ?? "-",
      "Registered Date": row["Registered Date"]
        ? convertUTCToLocal(row["Registered Date"])
        : "-",
      "Trolley ID": row["Trolley Id"] ?? "-",
      "Tote Priority": row["Tote Priority"] ?? "-",
      "Secondary location(From → To)":
        row["Secondary location(From → To)"] ?? "-",
      Tag: row?.Missed ? "Missed Scan" : row?.Delayed ? "Delayed" : "-",
      "Location In": row["Location In"]
        ? convertUTCToLocal(row["Location In"])
        : "-",
      "Location Out": row["Location Out"]
        ? convertUTCToLocal(row["Location Out"])
        : "-",
      "Case Type": row["Case Type"] ?? "-",
    }));

    // Insert Tote Table Headers
    toteHeaders.forEach((h, i) => {
      const addr = XLSX.utils.encode_cell({ r: blankRowIndex + 1, c: i });
      ws[addr] = {
        v: h,
        s: {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "4472C4" } },
          alignment: { horizontal: "center" },
        },
      };
    });

    // Insert Tote Table Rows
    toteRows.forEach((row: any, rIndex: any) => {
      toteHeaders.forEach((h, cIndex) => {
        const addr = XLSX.utils.encode_cell({
          r: blankRowIndex + 2 + rIndex,
          c: cIndex,
        });
        console.log(row, h);

        ws[addr] = { v: row[h] };
      });
    });

    // -------------------------------
    // Generate Workbook
    // -------------------------------
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Flow Report");

    const wbout = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    saveAs(
      new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Flow_View_${convertUTCToLocal(new Date())}.xlsx`
    );
  };

  const downloadXlsx = (data: any) => {
    const filteredData = data.map((row: any) => {
      // Check for "No of Totes in Trolley" in various possible field names
      const toteCount =
        row["No of Totes in Trolley"] ??
        row["No. of Totes in Trolley"] ??
        row["available_totes"] ??
        row["availableTotes"] ??
        row["total_totes"] ??
        row["totalTotes"] ??
        (row["totes"] && Array.isArray(row["totes"])
          ? row["totes"].length
          : null);

      return {
        "Primary Location": row["Primary Location"] ?? "-",
        "Trolley ID": row["Trolley ID"] ?? "-",
        "No of Totes in Trolley":
          toteCount !== null && toteCount !== undefined ? toteCount : "-",
        "Trolley Priority":
          row["Trolley Priority"] === "TRUE"
            ? "High"
            : row["Trolley Priority"] === "FALSE"
            ? "Standard"
            : row["Trolley Priority"] ?? "-",
        "Tote No": row["Tote No"] ?? "-",
        "Tote Priority": row["Tote Priority"] ?? "-",
        "Case Type": row["Case Type"] ?? "-",
      };
    });

    const worksheet: any = XLSX.utils.json_to_sheet(filteredData, {
      header: [
        "Primary Location",
        "Trolley ID",
        "No of Totes in Trolley",
        "Trolley Priority",
        "Tote No",
        "Tote Priority",
        "Case Type",
      ],
    });

    const columnWidths = [
      { wch: 25 },
      { wch: 15 },
      { wch: 22 },
      { wch: 18 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
    ];
    worksheet["!cols"] = columnWidths;

    const headerRange = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let C = headerRange.s.c; C <= headerRange.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
      const cell = worksheet[cellAddress];
      if (cell) {
        cell.s = {
          font: { bold: true, color: { rgb: "FFFFFFFF" } },
          fill: { fgColor: { rgb: "4472C4" } },
          alignment: { horizontal: "center" },
        };
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    const wbout = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    saveAs(
      new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Location_View_${convertUTCToLocal(new Date())}.xlsx`
    );
  };

  const downloadReportMutate = useMutation({
    mutationFn: (payload: any) => downloadLocationView(payload),
    onSuccess: (data) => {
      if (dashboardView === "Location") {
        downloadXlsx(data?.details);
      } else {
        downloadXlsxTrackView(data);
      }
    },
    onError: (response) => {
      notification?.error({ message: response?.message });
    },
  });

  useEffect(() => {
    if (dashboardView === "Flow" && localStorage?.getItem("flowFilter")) {
      setPayload({
        ...payload,
        search: localStorage?.getItem("flowFilter") || null,
      });
      setSearch(localStorage?.getItem("flowFilter") || null);
    }
  }, [dashboardView, localStorage]);

  return (
    <>
      <DashboardHeader />
      {filterOpen && (
        <DashboardFilters
          open={filterOpen}
          setOpen={setFilterOpen}
          payload={payload}
          setPayload={setPayload}
        />
      )}
      <div className={Styles.mainContainer}>
        <Row align="middle" justify="space-between">
          <Col span={5}>
            <Segmented
              options={dashboardOptions}
              className="dashboardSegments"
              value={dashboardView}
              onChange={(value) => {
                // setSearch(null);
                // localStorage?.removeItem("flowFilter");
                setDashboardView(value);
                localStorage?.setItem("dashboardTab", value);
              }}
            />
          </Col>
          <Col>
            <Row gutter={10} align="middle">
              <Col>
                <Input
                  prefix={<Icon component={SearchIcon} />}
                  placeholder="Search by Tote ID / Trolley ID"
                  style={{ width: "300px" }}
                  onChange={(e) => {
                    handleSearch(e);
                    setSearch(e?.target?.value);
                    if (!e?.target?.value) {
                      localStorage?.removeItem("flowFilter");
                    }
                  }}
                  allowClear
                  value={search}
                  enterKeyHint="search"
                  onPressEnter={(e) => {
                    // Trigger search immediately when search button is pressed
                    handleSearch(e);
                    // Close the keyboard
                    e.currentTarget.blur();
                  }}
                />
              </Col>
              <Col>
                <Select
                  value={autoRefreshInterval}
                  onChange={(value) => setAutoRefreshInterval(value)}
                  options={refreshIntervalOptions}
                  style={{ width: "120px" }}
                  suffixIcon={
                    <SyncOutlined
                      spin={f1 || f2}
                      style={{ color: "#1890ff" }}
                    />
                  }
                />
              </Col>
              <Col>
                <Badge
                  dot
                  count={
                    payload?.primary_location ||
                    payload?.end_date ||
                    payload?.start_date ||
                    payload?.tote_status ||
                    payload?.trolley_status ||
                    payload?.missed ||
                    payload?.delayed ||
                    payload?.use_start_date ||
                    payload?.use_end_date ||
                    payload?.delivery_start_date ||
                    payload?.delivery_end_date ||
                    payload?.priority_status
                      ? 1
                      : 0
                  }
                >
                  <Button
                    style={{ height: "35px" }}
                    icon={<Icon component={FilterIcon} />}
                    onClick={() => setFilterOpen(true)}
                  />
                </Badge>
              </Col>
              <Col>
                <Button
                  type="primary"
                  style={{ padding: "0 25px", height: "35px" }}
                  icon={<Icon component={DownloadIcon} />}
                  iconPosition="end"
                  onClick={() => {
                    downloadReportMutate?.mutate({
                      payload,
                      url:
                        dashboardView === "Location"
                          ? "downloadFlowView"
                          : "downloadLocationViewTrack",
                    });
                  }}
                >
                  Report
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
        <Spin spinning={l1 || l2 || f1 || f2} indicator={<CustomSpin />}>
          {dashboardView === "Location" ? (
            <LocationView d1={d1} l1={l1} setDashboardView={setDashboardView} />
          ) : (
            <FlowView d2={d2} payload={payload} setPayload={setPayload} />
          )}
        </Spin>
      </div>
    </>
  );
};

export default SupervisorDashboard;
